// utils/header-buttons.ts — Les boutons d'en-tête ne sont cliquables qu'une
// fois câblés.
//
// Défaut trouvé le 2026-09-10 en écrivant les e2e du parcours Programmes.
// Le motif de presque toutes les pages est :
//
//     setHeader(titre, '<button id="btn-new-program">…</button>');
//     setContent('<div class="loading">…');
//     const data = await api.getPrograms();   // ← ici, plusieurs centaines de ms
//     renderProgramsPage(data);               // ← c'est SEULEMENT ici qu'on câble
//
// Entre l'affichage du bouton et l'attache de son écouteur, le bouton est
// visible, actif, et parfaitement inerte : on clique, il ne se passe rien, et
// rien ne l'explique. 17 pages présentaient ce trou.
//
// La correction se pose une fois ici plutôt que dans chaque page : `setHeader`
// désactive les boutons qu'il vient d'écrire, et chacun se réactive au moment
// où on lui attache son écouteur de clic. Le signal est exactement le bon —
// « ce bouton fait maintenant quelque chose » — et aucune page n'a à changer.
//
// CONTRAT : un bouton d'en-tête se câble par un `addEventListener('click', …)`
// posé SUR LUI. Un bouton géré par délégation depuis un ancêtre resterait
// désactivé. Les 42 boutons d'en-tête du front respectent cette forme
// (vérifié au recensement) ; `tests/test_header_buttons_wiring.py` le tient.

/** Écouteur déjà attaché par le HTML lui-même (`onclick=`) : rien à attendre. */
function estDejaActionnable(btn: HTMLButtonElement): boolean {
  return btn.hasAttribute('onclick');
}

/**
 * Désactive les boutons de `root`, chacun jusqu'à ce qu'un écouteur de clic
 * lui soit attaché.
 *
 * Un bouton déjà `disabled` dans le HTML est laissé tel quel : la page l'a
 * voulu ainsi, ce n'est pas à cette fonction de le rouvrir.
 */
export function armHeaderButtons(root: ParentNode): void {
  root.querySelectorAll<HTMLButtonElement>('button:not([disabled])').forEach((btn) => {
    if (estDejaActionnable(btn)) return;
    btn.disabled = true;
    const attacheOrigine = btn.addEventListener.bind(btn) as typeof btn.addEventListener;
    btn.addEventListener = function (
      type: string,
      listener: EventListenerOrEventListenerObject,
      options?: boolean | AddEventListenerOptions,
    ): void {
      attacheOrigine(type, listener, options);
      if (type === 'click') {
        btn.disabled = false;
        // Une fois rendu, le bouton n'a plus rien à intercepter : on remet la
        // méthode native, pour que les écouteurs suivants soient ordinaires.
        btn.addEventListener = attacheOrigine;
      }
    } as typeof btn.addEventListener;
  });
}
